import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';

import { fetchProjectListingsCount } from '@/api/v4/project-listings.api';
import { QueryKey } from '@/config/query-client';
import { modalState } from '@/state/modal.state';
import { colors } from '@/theme/colors';
import { Button } from '@/ui/button/button';
import { Inline, Stack } from '@/ui/line/line';
import { projectIdState } from '@/features/projects/project.state';
import {
  useAutoEnhanceConfigQuery,
  useUpsertAutoEnhanceConfig,
} from './use-auto-enhance-config';
import {
  type HydratedLocation,
  useLocationHydration,
} from './use-location-hydration';
import { useProjectListings } from '@/features/projects/project-listings/use-project-listings.query';
import { useProject } from '@/features/projects/overview/use-project.query';
import { draftJsRawToPlainText } from '@/helpers/draftjs';
import { getTimeInMs } from '@/helpers/get-time-in-ms';
import {
  companyFilterSortState,
  WIZARD_STEPS,
  type WizardStep,
} from '@/features/enhance-common/constants/enhance.constants';
import {
  ConfigModal as Modal,
  Footer,
  StepText,
} from '@/features/enhance-common/styles/enhance-modal.styles';
import { EnhanceHeader } from '@/features/enhance-common/components/enhance-header';
import { ProblemDefinitionView } from '@/features/enhance-common/components/problem-definition-view';
import { CompanySelectionView } from '@/features/enhance-common/components/company-selection-view';
import { useCompanySelectionTable } from '@/features/enhance-common/hooks/use-company-selection-table';
import { useLocationOptions } from '@/features/enhance-common/hooks/use-location-options';

const EMPTY_HYDRATED_LOCATIONS: HydratedLocation[] = [];

export const AutoEnhanceConfigModal = () => {
  const { t } = useTranslation(['projects', 'default']);
  const setModalState = useSetAtom(modalState);
  const projectId = useAtomValue(projectIdState);
  const { data: config } = useAutoEnhanceConfigQuery(projectId ?? undefined);
  const { data: project } = useProject(projectId);
  const { mutate: saveConfig, isPending: isSaving } =
    useUpsertAutoEnhanceConfig(projectId ?? undefined);

  const [currentStep, setCurrentStep] = useState<WizardStep>(
    WIZARD_STEPS.PROBLEM_DEFINITION,
  );
  const [problemDescription, setProblemDescription] = useState('');
  const [desiredSolution, setDesiredSolution] = useState('');
  const [locations, setLocations] = useState<
    { label: string; value: string }[]
  >([]);
  const [excludedCompanies, setExcludedCompanies] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [companySearch, setCompanySearch] = useState('');

  const companyFilterSort = useAtomValue(companyFilterSortState);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, projectListings } =
    useProjectListings({
      projectId: projectId ?? '',
      search: companySearch,
      sort: companyFilterSort,
    });

  const { data: listingsCountData } = useQuery({
    enabled: Boolean(projectId),
    queryFn: () =>
      projectId ? fetchProjectListingsCount(projectId) : undefined,
    queryKey: [QueryKey.ProjectListingsCount, projectId, 'auto-enhance'],
    staleTime: getTimeInMs(5, 'minute'),
  });

  const totalListingsCount = listingsCountData?.total ?? 0;

  const { data: hydratedLocationsData } = useLocationHydration(
    config?.locations,
  );
  const hydratedLocations = hydratedLocationsData ?? EMPTY_HYDRATED_LOCATIONS;

  useEffect(() => {
    if (!config) {
      const plainText = project?.problemStatement
        ? draftJsRawToPlainText(project.problemStatement)
        : '';
      setProblemDescription(plainText);
      setDesiredSolution('');
      setLocations([]);
      return;
    }
    setProblemDescription(config.problemDescription ?? '');
    setDesiredSolution(config.desiredSolution ?? '');
    setLocations(hydratedLocations);
  }, [config, hydratedLocations, project?.problemStatement]);

  useEffect(() => {
    if (!config) return;

    if (Array.isArray(config.filterCompanies)) {
      // If projectListings are already loaded, filter to only include valid companies
      if (projectListings && projectListings.length > 0) {
        const allCompanyIds = projectListings.map(
          listing => listing.organization.id,
        );
        // Only include companies from filterCompanies that exist in projectListings
        const validExcludedCompanies = (
          config.filterCompanies as string[]
        ).filter(companyId => allCompanyIds.includes(companyId));
        setExcludedCompanies(validExcludedCompanies);
      } else {
        // Set directly if projectListings aren't loaded yet
        setExcludedCompanies(config.filterCompanies as string[]);
      }
    } else {
      setExcludedCompanies([]);
    }
  }, [config, projectListings]);

  const isValid =
    problemDescription.trim().length > 0 && problemDescription.length <= 2000;

  const hasProjectListings = (projectListings?.length ?? 0) > 0;

  const handleNext = () => {
    if (hasProjectListings) {
      setCurrentStep(WIZARD_STEPS.COMPANY_SELECTION);
    } else {
      handleSave();
    }
  };

  const handleBack = () => {
    setCurrentStep(WIZARD_STEPS.PROBLEM_DEFINITION);
  };

  const handleSave = () => {
    if (!projectId) return;
    saveConfig(
      {
        desiredSolution: desiredSolution || undefined,
        enabled: true,
        filterCompanies:
          excludedCompanies.length > 0 ? excludedCompanies : undefined,
        locations:
          locations.length > 0
            ? locations.map(loc => JSON.parse(loc.value))
            : undefined,
        problemDescription,
      },
      {
        onSuccess: () => setModalState(null),
      },
    );
  };

  const toggleCompanyExclusion = (companyId: string) => {
    setIsAllSelected(false);
    setExcludedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      }
      return [...prev, companyId];
    });
  };

  const handleSelectAllCompanies = (isChecked: boolean) => {
    if (!projectListings) return;
    setIsAllSelected(isChecked);
    if (isChecked) {
      // Select all = exclude none
      setExcludedCompanies([]);
    } else {
      // Deselect all = exclude all
      setExcludedCompanies(projectListings.map(l => l.organization.id));
    }
  };

  const { setOptions: setLocationOptions } = useLocationOptions();

  const companySelectionTable = useCompanySelectionTable({
    isAllSelected,
    isInverted: true,
    onSelectAll: handleSelectAllCompanies,
    onToggle: toggleCompanyExclusion,
    projectListings,
    selectedCompanies: excludedCompanies,
    totalCount: totalListingsCount,
  });

  const getStepIndicator = () => {
    if (currentStep === WIZARD_STEPS.PROBLEM_DEFINITION) {
      return hasProjectListings
        ? t`enhanceConfigStage1of2`
        : t`enhanceConfigStage1of1`;
    }
    if (currentStep === WIZARD_STEPS.COMPANY_SELECTION) {
      return t`enhanceConfigStage2of2`;
    }
    return null;
  };

  const getButtonText = () => {
    if (currentStep === WIZARD_STEPS.PROBLEM_DEFINITION) {
      return hasProjectListings ? t`enhanceConfigNext` : t`default:save`;
    }
    return t`default:save`;
  };

  const showBackButton = currentStep === WIZARD_STEPS.COMPANY_SELECTION;

  return (
    <Modal
      CustomHeaderComponent={
        showBackButton
          ? () => (
              <EnhanceHeader
                showBackButton={showBackButton}
                onBack={handleBack}
              />
            )
          : undefined
      }
      header={t`enhanceConfigHeader`}
      outsideClose={false}
    >
      <Stack gap="16px">
        {currentStep === WIZARD_STEPS.PROBLEM_DEFINITION && (
          <ProblemDefinitionView
            problemDescription={problemDescription}
            desiredSolution={desiredSolution}
            locations={locations}
            onProblemDescriptionChange={setProblemDescription}
            onDesiredSolutionChange={setDesiredSolution}
            onLocationsChange={setLocations}
            setLocationOptions={setLocationOptions}
            dataTestId="auto-enhance-problem-definition-view"
          />
        )}

        {currentStep === WIZARD_STEPS.COMPANY_SELECTION && (
          <CompanySelectionView
            dataTestId="auto-enhance-company-selection-view"
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            headers={companySelectionTable.headers}
            isFetchingNextPage={isFetchingNextPage}
            onSearchChange={setCompanySearch}
            projectListings={projectListings ?? []}
            rows={companySelectionTable.rows}
            searchValue={companySearch}
            sortState={companyFilterSortState}
          />
        )}

        <Footer name="auto-enhance-config-footer" justify="space-between">
          <Inline gap="12px">
            <Button
              variant="secondary"
              onClick={() => setModalState(null)}
              data-testid="auto-enhance-cancel"
            >
              {t`default:cancel`}
            </Button>
          </Inline>

          <Inline gap="12px" alignItems="center">
            {getStepIndicator() && (
              <StepText data-testid="auto-enhance-step-indicator">
                {getStepIndicator()}
              </StepText>
            )}
            <Button
              data-testid="auto-enhance-next"
              variant="primary"
              endIcon={
                currentStep === WIZARD_STEPS.PROBLEM_DEFINITION
                  ? 'ArrowRight'
                  : undefined
              }
              height={32}
              disabled={!isValid || isSaving}
              iconColor={colors.basics.white}
              onClick={
                currentStep === WIZARD_STEPS.PROBLEM_DEFINITION
                  ? handleNext
                  : handleSave
              }
            >
              {getButtonText()}
            </Button>
          </Inline>
        </Footer>
      </Stack>
    </Modal>
  );
};
